import { useState, useEffect, useRef } from 'react';
import { useAuthHttp } from '@hooks';
import { PREVIEW_PRODUCT_ALLOCATION } from '@constants';

export type ProductAllocationPackaging = {
  packagingId: string;
  type: string;
  skusPerUnit: number;
  quantity: number;
};

export type ProductAllocationSku = {
  skuId: string;
  skuIdentifier: string;
  name: string;
  unitOfMeasure: string;
  productsPerSku: number;
  wholeUnitsToPick: number;
  unitsToBreak: number;
  looseToTake: number;
  productsToShip: number;
  packaging: ProductAllocationPackaging[];
};

export type ProductAllocation = {
  productId: string;
  requestedQuantity: number;
  hasEnoughStock: boolean;
  error: string | null;
  totalProductsShipped: number;
  totalLooseCreated: number;
  totalWholeUnitsToPick: number;
  totalUnitsToBreak: number;
  skus: ProductAllocationSku[];
};

export type ProductAllocationPreviewResponse = {
  products: ProductAllocation[];
};

export type AllocationPreviewSkuLine = {
  skuId: string;
  skuQuantity: number;
};

export type AllocationPreviewProductLine = {
  productId: string;
  productQuantity: number;
};

export const useCombinedAllocationPreview = (
  warehouseId: string | undefined,
  skuLines: AllocationPreviewSkuLine[],
  productLines: AllocationPreviewProductLine[]
) => {
  const authHttp = useAuthHttp();

  const [previews, setPreviews] = useState<ProductAllocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previousController = useRef<AbortController>();
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  const skuLinesKey = JSON.stringify(skuLines);
  const productLinesKey = JSON.stringify(productLines);
  const currentKey = `${warehouseId ?? ''}|${skuLinesKey}|${productLinesKey}`;

  const [settledKey, setSettledKey] = useState<string | null>(null);

  useEffect(() => {
    if (!warehouseId || productLines.length === 0) {
      setPreviews([]);
      setError(null);
      setLoading(false);
      setSettledKey(currentKey);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const fetchPreview = async () => {
      if (previousController.current) {
        previousController.current.abort();
      }
      const controller = new AbortController();
      const { signal } = controller;
      previousController.current = controller;

      try {
        const { data } = await authHttp.post<ProductAllocationPreviewResponse>(
          PREVIEW_PRODUCT_ALLOCATION,
          {
            warehouseId,
            skuLines,
            lines: productLines,
          },
          { signal }
        );

        setPreviews(data.products || []);
        setSettledKey(currentKey);
      } catch (caughtError: any) {
        if (caughtError?.code === 'ERR_CANCELED') return;
        setPreviews([]);
        setError('Could not load the allocation preview.');
        // eslint-disable-next-line no-console
        console.error(caughtError);
      } finally {
        setLoading(false);
      }
    };

    debounceTimer.current = setTimeout(fetchPreview, 350);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      previousController.current?.abort();
    };
  }, [warehouseId, skuLinesKey, productLinesKey]);

  return { previews, loading, error, stale: currentKey !== settledKey };
};
